import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';


/** Scope ordinary product-name searches to titles, not shared description copy.
 * Keep explicit Shopify search syntax available for advanced queries.
 */
function productNameQuery(value) {
  const terms = value.trim();
  if (!terms || /[:"()*]|\b(?:AND|OR|NOT)\b/.test(terms)) return terms;
  return terms.split(/\s+/).map(term => `title:${term}`).join(' ');
}

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];
  #submitController;

  connectedCallback() {
    super.connectedCallback();
    this.#submitController?.abort();
    this.#submitController = new AbortController();
    this.refs.searchPageInput.form?.addEventListener('submit', this.submitProductSearch, { signal: this.#submitController.signal });
  }

  disconnectedCallback() {
    this.#submitController?.abort();
    super.disconnectedCallback();
  }

  /** @param {SubmitEvent} event */
  submitProductSearch = (event) => {
    event.preventDefault();
    const form = this.refs.searchPageInput.form;
    if (!form) return;
    const url = new URL(form.action, location.origin);
    url.searchParams.set('q', productNameQuery(this.refs.searchPageInput.value));
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    window.location.assign(url.toString());
  };

  /**
   * Handles the click event on the clear button and submits an empty search.
   * This clears the search input and resubmits the form if the page is not
   * already in an empty state.
   */
  handleClearClick() {
    this.#submitEmptySearch();
  }

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}

